import React from 'react';
import { connect } from 'react-redux';
import { IBulletin, ICommercialBulletin, PoiId, ResolutionPreference } from 'utils/entities';
import { ItemWrapper } from 'ds/components/carousels/carousel/styled';
import { Link } from 'components/link';
import { routeByPoiType } from 'utils/marketplaceRoutes';
import { ShowAt } from 'consts/breakpoints';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import Carousel from 'ds/components/carousels/carousel';
import { makeByIdsDictionarySelector } from 'store/state/domainData/selectors/common';
import { State } from 'store/state';
import { makeMapStateToPropsFactory } from 'components/cards/listing/makeMapStateToPropsFactory';
import { UniversalCardProps } from 'components/cards/listing/UniversalCard/UniversalCard';
import UniversalCard from 'components/cards/listing/UniversalCard';
import {
  isAdditionalListingCarouselLoadingSelector,
  relatedAgentListingsSelector,
} from 'store/state/domainData/selectors/additionalListingCarousel';
import { useLocale } from 'locale';
import { H3 } from 'ds/components/typography';
import styled from '@emotion/styled';
import withEvents from 'analytics/withEvents';
import { CardType } from 'components/cards/types';


interface PrivateListingCarouselProps {
  listings: Array<IBulletin | ICommercialBulletin>;
  isLoading: boolean;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onScrollLeft?: () => void;
  onScrollRight?: () => void;
}

const listingsByIdsSelector = makeByIdsDictionarySelector(relatedAgentListingsSelector);
const poiByIdsSelector: any = (state: State, poiId: PoiId) => listingsByIdsSelector(state)[poiId];

const makeMapStateToProps = makeMapStateToPropsFactory(poiByIdsSelector);

const SpecificUniversalCard = (props: UniversalCardProps) => {
  let enhancedProps: UniversalCardProps = {
    ...props,
    hideLogoPreview: true,
    isMinHeight: true,
  };
  if (enhancedProps.cardType === CardType.Bulletin || enhancedProps.cardType === CardType.CommercialBulletin) {
    enhancedProps = {
      ...enhancedProps,
      resolutionPreferences: ResolutionPreference.Neighborhood,
    };
  }
  return <UniversalCard {...enhancedProps} />;
};

const ConnectedUniversalCard = connect(makeMapStateToProps)(SpecificUniversalCard);

const KEY = Symbol('privateListingsCarousel');

const Header = styled(H3.withComponent('h2'))`
  ${({ theme }) => `
    margin-bottom: ${theme.spacing(2)};
  `}
`;

const PrivateListingCarousel: React.FC<PrivateListingCarouselProps> = (props) => {
  const { listings, isLoading, onScrollLeft, onScrollRight, onSwipeLeft, onSwipeRight } = props;
  const { t } = useLocale();

  const hasArrows = listings.length > 2;

  if (!listings || listings.length === 0) return null;

  const listingsList = listings.map(({ id, type }) => (
    <ItemWrapper key={id}>
      <Link
        routeName={routeByPoiType(type)}
        routeParams={{ id, tracking_event_source: 'property_carousel' }}
        hideLinkParams
        target="_blank"
      >
        <ConnectedUniversalCard
          id={id}
          emptyLazyLoadPlaceholder
        />
      </Link>
    </ItemWrapper>
  ));

  return (
    <>
      <Header weight="bold">{t('unitPage.moreAdsForU')}</Header>
      <ShowAt to={2}>
        <ScrollBox
          cacheKey={KEY}
          autoWidth
          onSwipeLeft={onSwipeLeft}
          onSwipeRight={onSwipeRight}
        >
          {listingsList}
        </ScrollBox>
      </ShowAt>
      <ShowAt from={3}>
        <Carousel
          isLoading={isLoading}
          hasArrows={hasArrows}
          onScrollLeft={onScrollLeft}
          onScrollRight={onScrollRight}
        >
          {listingsList}
        </Carousel>
      </ShowAt>
    </>
  );
};

const PrivateListingCarouselWithEvents = withEvents<PrivateListingCarouselProps>((sendEvent) => ({
  onSwipeLeft() {
    sendEvent('property_carousel_scroll', 'property', {
      event: {
        carousel_type: 'similar_listings',
        direction: 'left',
        action: 'swipe',
      },
    });
  },
  onSwipeRight() {
    sendEvent('property_carousel_scroll', 'property', {
      event: {
        carousel_type: 'similar_listings',
        direction: 'right',
        action: 'swipe',
      },
    });
  },
  onScrollLeft() {
    sendEvent('property_carousel_scroll', 'property', {
      event: {
        carousel_type: 'similar_listings',
        direction: 'left',
        action: 'click',
      },
    });
  },
  onScrollRight() {
    sendEvent('property_carousel_scroll', 'property', {
      event: {
        carousel_type: 'similar_listings',
        direction: 'right',
        action: 'click',
      },
    });
  },
}))(PrivateListingCarousel);

const mapStateToProps = (state: State) => ({
  isLoading: isAdditionalListingCarouselLoadingSelector(state),
  listings: relatedAgentListingsSelector(state),
});

export default connect(mapStateToProps)(PrivateListingCarouselWithEvents);
